// useSearchChord — the ONE keyboard chord the web build registers: ⌘K (Ctrl+K
// off mac) opens the Search page with its field focused (owner, 3 Nov; IDD
// 73062339, superseding dfd31976: one way to find things, the omnibar is
// gone).
//
// Chords are otherwise DESKTOP-ONLY, and the desktop shell is gone: the web
// build registers ZERO other chrome-level keyboard shortcuts, so it never
// claims or `preventDefault`s a chord that would overshadow the browser's own
// (Cmd+T / Cmd+W / Cmd+L / Cmd+1–9 …). Cmd+K is the single ratified exception
// (CLAUDE.md): it is not browser-reserved.

import { useEffect } from "react";
import type { KbdPlatform } from "../../components/Kbd/Kbd.js";
import type { TriggerSignal } from "../trigger.js";

export type SearchChordDeps = {
    // The ONE stamped door — the shell stamps the clock and routes. The ⌘K
    // press fires the `open-search` UiIntent through it, as the menu's Search
    // item does.
    readonly applyTrigger: (signal: TriggerSignal) => void;
    // Which platform's primary modifier opens Search (⌘ on mac, Ctrl else).
    readonly kbdPlatform: KbdPlatform;
};

// Is this key press the Search chord? Pure: the primary modifier alone
// (⌘ on mac and not a literal Ctrl too; Ctrl elsewhere and not ⌘) with K.
const isSearchChord = (
    e: Pick<
        KeyboardEvent,
        "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey"
    >,
    platform: KbdPlatform,
): boolean => {
    if (e.altKey || e.shiftKey) return false;
    const mod =
        platform === "mac" ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
    return mod && e.key.toLowerCase() === "k";
};

export const useSearchChord = ({
    applyTrigger,
    kbdPlatform,
}: SearchChordDeps): void => {
    // No dependency array on purpose: re-subscribing every render keeps the
    // closed-over `applyTrigger` fresh.
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (!isSearchChord(e, kbdPlatform)) return;
            // A mod-chord types no character, so no focus guard is needed (it
            // fires from an input too).
            e.preventDefault();
            applyTrigger({
                provenance: { type: "user" },
                action: { type: "key", action: { type: "open-search" } },
            });
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    });
};
